import {
  cn,
  formatMessageTimestamp,
  isValidLink,
  timeDiffFromNow,
} from "@/lib/utils";
import { MessageEntity, MessageSendingStatus } from "@/indexedDB/types";

import MessageAvatar from "./MessageAvatar";
import MessageReactions from "./reactions/MessageReactions";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/primitives/context-menu";

import { IS_MOBILE } from "@/lib/environmentDetector";
import { ALLOWED_REACTIONS, MESSAGE_EDIT_TTL } from "@/config/constants";
import { useMessageActions } from "./hooks/useMessageActions";

import MessageAttachment from "./attachments/MessageAttachment";

import MessageLink from "./MessageLink";
import MessageText from "./MessageText";
import { useTranslation } from "react-i18next";
import { useRtfInputState } from "@/zustand/RtfInputState";
import { Copy, Pencil } from "lucide-react";
import { Separator } from "@/components/primitives/separator";

import ParentMessage from "./ParentMessage";
import MessageTimestamp from "./MessageTimestamp";

type Props = {
  message: MessageEntity;
};

export default function ThreadMessage({ message }: Props) {
  const { t } = useTranslation();
  const { handleReactToMessage, handleCopyTextToClipboard } =
    useMessageActions();

  const msgTTL = timeDiffFromNow(message.timestamp, "minutes");

  // RTF input state for editing and replying
  const setEditMessage = useRtfInputState((state) => state.setEditMessage);

  const clearReplyMessage = useRtfInputState(
    (state) => state.clearReplyMessage,
  );
  const clearDraftMessage = useRtfInputState(
    (state) => state.clearDraftMessage,
  );

  const handleEdit = () => {
    setEditMessage(message.chatId, message);
    clearReplyMessage(message.chatId);
    clearDraftMessage(message.chatId);
  };

  const editIsWithinTTL = msgTTL !== null && msgTTL < MESSAGE_EDIT_TTL;

  const canCopy = !!message.content.text;

  const isMessageStatusOk =
    message.sendingStatus === MessageSendingStatus.DELIVERED ||
    message.sendingStatus === MessageSendingStatus.READ;
  const canReact = isMessageStatusOk;

  const canEdit =
    message.isOutgoing &&
    isMessageStatusOk &&
    editIsWithinTTL &&
    (message.content.text || message.content.media || message.content.file);

  return (
    <div className="contents">
      <div
        data-message-id={message.id}
        className="flex flex-col px-6 pt-10 3xl:px-0"
      >
        <div className="flex w-full max-w-96 gap-2 lg:max-w-[440px]">
          <div className="mt-1 size-8">
            <MessageAvatar
              sender={message.sender}
              avatarId={message.avatarId || ""}
            />
          </div>

          <div className={cn("flex flex-col")}>
            <div
              className={cn(
                "truncate text-sm font-bold leading-snug tracking-tighter text-primary",
              )}
            >
              {message.sender}
            </div>
            <ContextMenu modal={false}>
              <ContextMenuTrigger
                className={cn(
                  "grid gap-1 overflow-hidden rounded-md p-1",
                  message.isOutgoing
                    ? "bg-primary text-right text-primary-foreground"
                    : "bg-accent text-left text-accent-foreground",
                )}
              >
                <>
                  {/* PARENT MESSAGE — resolved by id so the quote shows the
                      PARENT's sender/content (the old inline block mirrored
                      this message's own fields, mis-attributing the quote —
                      visibly wrong once senders can be anonymised). */}
                  {message.parentMessage && (
                    <ParentMessage
                      id={message.parentMessage.id}
                      chatId={message.chatId}
                      className="border-l-4 border-secondary/40 bg-card/60"
                    />
                  )}

                  {/* MESSAGE ATTACHMENT */}
                  {message.hasAttachment && (
                    <div
                      className={cn(
                        "relative select-none justify-self-start",
                        // message.content.media
                        //   ? `border-4 rounded-sm ${message.isOutgoing ? "border-primary " : "border-accent "}`
                        //   : "",
                      )}
                    >
                      <MessageAttachment
                        messageId={message.id}
                        messageTimestamp={message.timestamp}
                        chatId={message.chatId}
                        messageContent={message.content}
                        isOutgoingMessage={message.isOutgoing}
                        // ThreadMessage only renders the thread's PARENT (root)
                        // message, whose own id IS the thread id. Scope its
                        // attachment to the thread (not message.threadId, which
                        // is null for a main-chat message → would open the whole
                        // main chat's media). getViewerMediaAttachments prepends
                        // this parent to the thread's carousel.
                        threadId={message.id}
                      />

                      {!message.content.text.length &&
                        !message.content.file && (
                          // <div
                          //   className={cn(
                          //     "flex items-center justify-end gap-1 align-middle text-[0.7rem]",
                          //   )}
                          // >
                          //   <div
                          //     className={cn(
                          //       "flex gap-1 rounded-md px-1",
                          //       message.isOutgoing
                          //         ? "bg-primary/20 dark:bg-primary dark:text-primary-foreground"
                          //         : "bg-accent text-accent-foreground",
                          //     )}
                          //   >
                          //     <span className="opacity-70">
                          //       {formatMessageTimestamp(message.timestamp)}
                          //     </span>
                          //   </div>
                          // </div>
                          <MessageTimestamp
                            className={cn(
                              "absolute bottom-2 right-2 select-none",
                              message.content.voice && "bottom-0 right-0",
                            )}
                            timestamp={message.timestamp}
                            isOutgoing={message.isOutgoing}
                            isPinned={message.isPinned}
                            sendingStatus={message.sendingStatus}
                            deliveryUnconfirmed={message.deliveryUnconfirmed}
                          />
                        )}
                    </div>
                  )}

                  {/* MESSAGE LINK  */}
                  {isValidLink(message.content.link) &&
                    !message.hasAttachment && <MessageLink message={message} />}

                  {/* MESSAGE TEXT  */}
                  {message.content.text && (
                    <MessageText
                      text={message.content.text}
                      isOutgoingMessage={message.isOutgoing}
                      isPinned={message.isPinned}
                      hasMediaAttachment={!!message.content.media}
                      sendingStatus={message.sendingStatus}
                      deliveryUnconfirmed={message.deliveryUnconfirmed}
                      timestamp={message.timestamp}
                      tags={message.tags}
                    />
                  )}

                  {/* MESSAGE TIMESTAMP  */}
                  {message.content.file && !message.content.text.length && (
                    <>
                      <div
                        className={cn(
                          "flex items-center justify-end gap-1 align-middle text-[0.7rem]",
                        )}
                      >
                        <div
                          className={cn(
                            "flex gap-1 rounded-md px-1",
                            message.isOutgoing
                              ? "bg-primary/20 dark:bg-primary dark:text-primary-foreground"
                              : "bg-accent text-accent-foreground",
                          )}
                        >
                          <span className="opacity-70">
                            {formatMessageTimestamp(message.timestamp)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </>
              </ContextMenuTrigger>

              <ContextMenuContent
                collisionBoundary={document.body}
                alignOffset={10}
                collisionPadding={10}
                sticky="always"
                backdrop={IS_MOBILE ? "blur" : "transparent"}
                className="flex flex-col gap-2 border-none bg-transparent shadow-none outline-none drop-shadow-md"
              >
                {canReact && (
                  <div className="custom-scroll custom-scroll-x flex max-w-[var(--radix-context-menu-content-available-width)] flex-row items-center gap-0 overflow-auto rounded-lg bg-card p-2 align-middle">
                    {ALLOWED_REACTIONS.map((reaction) => (
                      <ContextMenuItem
                        key={reaction}
                        onClick={() => handleReactToMessage(message, reaction)}
                        className="lg:cursor-pointer"
                      >
                        {reaction}
                      </ContextMenuItem>
                    ))}
                  </div>
                )}

                <div className="flex min-w-32 flex-col gap-0 self-end rounded-lg bg-card p-2">
                  {canCopy && (
                    <ContextMenuItem
                      onClick={() =>
                        handleCopyTextToClipboard(message.content.text)
                      }
                      className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
                    >
                      <Copy className="size-4" />
                      {t("copy")}
                    </ContextMenuItem>
                  )}

                  {canEdit && (
                    <ContextMenuItem
                      onClick={handleEdit}
                      className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
                    >
                      <Pencil className="size-4" />
                      {t("edit")}
                    </ContextMenuItem>
                  )}
                </div>
              </ContextMenuContent>
            </ContextMenu>
            <div
              className={cn(
                "mb-2 mt-1 flex w-full items-center gap-2",
                message.isOutgoing ? "justify-end" : "justify-start",
              )}
            >
              <MessageReactions parentMessage={message} />
            </div>
          </div>
        </div>
        <div className="mt-7 flex items-center gap-3">
          <div className="shrink-0 text-sm text-card-foreground">
            {message.commentCount ? message.commentCount : 0}{" "}
            {t("threads.comments")}
          </div>
          <Separator className="h-px shrink" />
        </div>
      </div>
    </div>
  );
}
